import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Play, CalendarClock, MessageSquare, Loader2, CheckCircle2, Star } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { Input } from "../ui/input";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import PayButton from "./PayButton";

function ReasonDialog({ open, onClose, title, desc, confirmLabel, onConfirm, danger }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await onConfirm(reason); onClose(); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-extrabold">{title}</DialogTitle><DialogDescription>{desc}</DialogDescription></DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan (opsional)" rows={3} data-testid="reason-input" />
        <Button onClick={go} disabled={busy} data-testid="reason-confirm-btn" className={`w-full font-bold rounded-xl h-11 text-white ${danger ? "bg-red-500 hover:bg-red-600" : "bg-blue-600 hover:bg-blue-700"}`}>
          {busy && <Loader2 size={16} className="animate-spin mr-2" />}{confirmLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function RescheduleDialog({ open, onClose, order, onConfirm }) {
  const [date, setDate] = useState(order.scheduled_date);
  const [time, setTime] = useState(order.scheduled_time);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); try { await onConfirm({ scheduled_date: date, scheduled_time: time, reason }); onClose(); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="reschedule-dialog">
        <DialogHeader><DialogTitle className="font-extrabold">Ajukan Jadwal Baru</DialogTitle><DialogDescription>Pihak lain perlu menyetujui jadwal baru sebelum berlaku.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-bold text-slate-600">Tanggal</label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="reschedule-date" /></div>
          <div><label className="text-xs font-bold text-slate-600">Jam</label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="reschedule-time" /></div>
        </div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan reschedule" rows={2} data-testid="reschedule-reason" />
        <Button onClick={go} disabled={busy} data-testid="reschedule-submit-btn" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-11">{busy && <Loader2 size={16} className="animate-spin mr-2" />}Kirim Permintaan</Button>
      </DialogContent>
    </Dialog>
  );
}

export function ReviewForm({ order, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/orders/${order.order_id}/review`, { rating, comment }); toast.success("Terima kasih atas ulasan Anda!"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="review-form">
      <div className="flex gap-1.5">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} onClick={() => setRating(s)} data-testid={`rating-star-${s}`} className="transition-transform hover:scale-110">
            <Star size={28} className={s <= rating ? "text-amber-400" : "text-slate-200"} fill={s <= rating ? "#fbbf24" : "none"} />
          </button>
        ))}
      </div>
      <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Bagikan pengalaman Anda dengan inspector ini..." className="mt-3 text-sm" data-testid="review-comment-input" />
      <Button onClick={submit} disabled={busy} data-testid="submit-review-btn" className="mt-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-10 px-5 text-sm">{busy && <Loader2 size={14} className="animate-spin mr-2" />}Kirim Ulasan</Button>
    </div>
  );
}

export default function OrderActions({ order, role, base }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");
  const refresh = () => { qc.invalidateQueries({ queryKey: ["order", order.order_id] }); qc.invalidateQueries({ queryKey: ["orders"] }); qc.invalidateQueries({ queryKey: ["inspector-dashboard"] }); qc.invalidateQueries({ queryKey: ["user-dashboard"] }); };
  const act = async (path, body = {}, ok) => {
    setBusy(path);
    try { await api.post(`/orders/${order.order_id}/${path}`, body); if (ok) toast.success(ok); refresh(); }
    catch (e) { toast.error(errMsg(e)); throw e; } finally { setBusy(""); }
  };
  const s = order.status;
  const req = order.reschedule_request;
  const pendingReq = req?.status === "pending";
  const canRespond = pendingReq && req.requested_by !== role;
  const canReschedule = ["menunggu_konfirmasi", "dijadwalkan"].includes(s) && !pendingReq && role !== "admin";
  const btn = "w-full font-bold rounded-xl h-10 text-sm";

  return (
    <div className="space-y-2.5" data-testid="order-actions">
      {pendingReq && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="reschedule-request-box">
          <p className="font-bold text-amber-700 flex items-center gap-1.5"><CalendarClock size={14} /> Permintaan reschedule ({req.requested_by})</p>
          <p className="text-amber-700 mt-1">Jadwal baru: <b>{fmtDate(req.scheduled_date)} · {req.scheduled_time}</b>{req.reason && ` — ${req.reason}`}</p>
          {canRespond && (
            <div className="flex gap-2 mt-2.5">
              <Button size="sm" onClick={() => act("reschedule/respond", { accept: true }, "Jadwal baru disetujui")} data-testid="reschedule-accept-btn" className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold h-8 rounded-lg">Setujui</Button>
              <Button size="sm" variant="outline" onClick={() => act("reschedule/respond", { accept: false }, "Jadwal baru ditolak")} data-testid="reschedule-reject-btn" className="flex-1 text-xs font-bold h-8 rounded-lg">Tolak</Button>
            </div>
          )}
          {!canRespond && <p className="text-[11px] text-amber-600 mt-1">Menunggu respons pihak lain.</p>}
        </div>
      )}

      {role === "pengguna" && s === "menunggu_bayar" && <PayButton order={order} />}
      {role === "inspector" && s === "menunggu_konfirmasi" && (
        <>
          <Button onClick={() => act("accept", {}, "Pesanan dikonfirmasi & dijadwalkan")} disabled={!!busy} data-testid="accept-order-btn" className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}><Check size={16} className="mr-1.5" /> Terima Pesanan</Button>
          <Button variant="outline" onClick={() => setDialog("reject")} data-testid="reject-order-btn" className={`${btn} text-red-600 border-red-200 hover:bg-red-50`}><X size={16} className="mr-1.5" /> Tolak Pesanan</Button>
        </>
      )}
      {role === "inspector" && s === "dijadwalkan" && (
        <Button onClick={() => act("start", {}, "Inspeksi dimulai. Isi checklist di bawah.")} disabled={!!busy} data-testid="start-inspection-btn" className={`${btn} bg-violet-600 hover:bg-violet-700 text-white`}><Play size={16} className="mr-1.5" /> Mulai Inspeksi</Button>
      )}
      {role === "pengguna" && s === "menunggu_review" && (
        <Button onClick={() => act("complete", {}, "Pesanan selesai. Dana diteruskan ke inspector.")} disabled={!!busy} data-testid="complete-order-btn" className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}><CheckCircle2 size={16} className="mr-1.5" /> Terima Laporan & Selesaikan</Button>
      )}
      {canReschedule && (
        <Button variant="outline" onClick={() => setDialog("reschedule")} data-testid="reschedule-btn" className={`${btn} border-slate-200 text-slate-700`}><CalendarClock size={16} className="mr-1.5" /> Ajukan Reschedule</Button>
      )}
      {s !== "menunggu_bayar" && s !== "dibatalkan" && (
        <Button variant="outline" onClick={() => navigate(`${base}/chat/${order.order_id}`)} data-testid="open-chat-btn" className={`${btn} border-blue-200 text-blue-600 hover:bg-blue-50`}><MessageSquare size={16} className="mr-1.5" /> Chat {role === "inspector" ? "Pengguna" : "Inspector"}</Button>
      )}
      {((role === "pengguna" && ["menunggu_bayar", "menunggu_konfirmasi", "dijadwalkan"].includes(s)) ||
        (role === "inspector" && s === "dijadwalkan") ||
        (role === "admin" && !["selesai", "dibatalkan", "menunggu_review"].includes(s))) && (
        <Button variant="ghost" onClick={() => setDialog("cancel")} data-testid="cancel-order-btn" className={`${btn} text-red-500 hover:bg-red-50 hover:text-red-600`}>Batalkan Pesanan</Button>
      )}

      <ReasonDialog open={dialog === "reject"} onClose={() => setDialog(null)} title="Tolak Pesanan" desc="Dana pengguna akan dikembalikan otomatis." confirmLabel="Tolak Pesanan" danger
        onConfirm={(reason) => act("reject", { reason }, "Pesanan ditolak, dana dikembalikan")} />
      <ReasonDialog open={dialog === "cancel"} onClose={() => setDialog(null)} title="Batalkan Pesanan" desc={s === "menunggu_bayar" ? "Pesanan belum dibayar dan akan dibatalkan." : "Dana yang sudah dibayar akan dikembalikan."} confirmLabel="Ya, Batalkan" danger
        onConfirm={(reason) => act("cancel", { reason }, "Pesanan dibatalkan")} />
      {dialog === "reschedule" && <RescheduleDialog open onClose={() => setDialog(null)} order={order} onConfirm={(body) => act("reschedule", body, "Permintaan reschedule dikirim")} />}
    </div>
  );
}
